import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Bell, Check, CheckCheck } from "lucide-react";

const POLL_MS = 20000;

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const nav = useNavigate();

  const reload = async () => {
    try {
      const r = await api.get("/notifications", { params: { limit: 20 } });
      setItems(r.data.items || []);
      setUnread(r.data.unread_count || 0);
    } catch {
      /* swallow — bell is non-critical */
    }
  };

  useEffect(() => {
    reload();
    const t = setInterval(reload, POLL_MS);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const onItemClick = async (n) => {
    if (!n.read_at) {
      try {
        await api.post(`/notifications/${n.id}/read`);
      } catch {
        /* ignore */
      }
    }
    setOpen(false);
    if (n.link) nav(n.link);
    reload();
  };

  const onMarkAll = async () => {
    try {
      await api.post("/notifications/read-all");
      reload();
    } catch {
      /* ignore */
    }
  };

  const fmtTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="notifications-bell"
        aria-label="Notifications"
        className="relative flex items-center justify-center w-9 h-9 bg-[#141417] border border-zinc-800 hover:border-zinc-600 rounded-md text-zinc-300 hover:text-white transition-colors"
      >
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <span
            data-testid="notifications-unread-count"
            className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-[#EF4444] text-white text-[9px] font-mono font-bold flex items-center justify-center"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notifications-panel"
          className="absolute right-0 top-full mt-1 w-[22rem] max-h-[28rem] overflow-y-auto bg-[#141417] border border-zinc-800 rounded-md shadow-2xl z-30"
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-800 sticky top-0 bg-[#141417]">
            <span className="text-[10px] uppercase font-mono tracking-widest text-zinc-500">
              Notifications {unread > 0 && <span className="text-[#D4A968]">· {unread} new</span>}
            </span>
            {unread > 0 && (
              <button
                onClick={onMarkAll}
                data-testid="notifications-mark-all"
                className="flex items-center gap-1 text-[10px] uppercase font-mono tracking-widest text-zinc-400 hover:text-[#D4A968]"
              >
                <CheckCheck className="w-3 h-3" /> Mark all read
              </button>
            )}
          </div>

          {items.length === 0 ? (
            <div className="p-6 text-xs text-zinc-500 text-center" data-testid="notifications-empty">
              You're all caught up.
            </div>
          ) : (
            <div className="divide-y divide-zinc-800/70">
              {items.map((n) => (
                <button
                  key={n.id}
                  onClick={() => onItemClick(n)}
                  data-testid={`notification-${n.id}`}
                  className={`w-full text-left px-3 py-2.5 hover:bg-[#1B1B1F] transition-colors ${
                    !n.read_at ? "bg-[#0B0B0E]" : ""
                  }`}
                >
                  <div className="flex items-start gap-2">
                    {!n.read_at && (
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#D4A968] shrink-0" />
                    )}
                    {n.read_at && (
                      <Check className="w-3 h-3 text-zinc-600 mt-1 shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-zinc-300">
                        <span className="font-semibold text-white">{n.actor_name || "Someone"}</span>{" "}
                        mentioned you in <span className="text-zinc-200">{n.context}</span>
                      </div>
                      {n.text && (
                        <div className="text-[11px] text-zinc-400 italic truncate mt-0.5">"{n.text}"</div>
                      )}
                      <div className="text-[10px] font-mono tracking-wider text-zinc-500 mt-1">
                        {fmtTime(n.created_at)}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          <button
            onClick={() => { setOpen(false); nav("/notifications"); }}
            data-testid="notifications-view-all"
            className="w-full sticky bottom-0 bg-[#141417] border-t border-zinc-800 px-3 py-2 text-[10px] uppercase font-mono tracking-widest text-zinc-400 hover:text-[#D4A968] hover:bg-[#1B1B1F] transition-colors"
          >
            View all notifications →
          </button>
        </div>
      )}
    </div>
  );
}
